import { Box, Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import {
    EVAC_AC_ONLY_REG,
    EVAC_ADDITIONAL_COMMENTS_REG,
    EVAC_BARCODE_REG,
    EVAC_MANUFACTURER_REG,
    EVAC_MAX_CURRENT_REG,
    EVAC_MODEL_REF_REG,
    EVAC_MODEL_REG,
    EVAC_PHASES_REG,
    EVAC_V2G_CAPABILITY_REG,
    EVAC_V2G_COMPLETION_DATE_REG,
    EVAC_V2G_EXPORT_CAPACITY_REG,
    EVAC_V2G_G99_REG,
    EVAC_V2G_TTR_STATUS_REG,
    EVAC_VOLTAGE_REG,
    evacDeviceFormLabelMapping,
    v2gG99Options,
} from "../../../../../constants/lct/evac/deviceForm"
import { POSITIVE_DECIMAL_REGEX } from "../../../../../constants/patterns"
import {
    TT_DEVICE_PHASES,
    TT_EVAC_BARCODE,
    TT_EVAC_MAX_CURRENT,
    TT_EVAC_MODEL_REF,
    TT_MODEL,
    TT_VOLTAGE,
} from "../../../../../constants/tooltips"
import { TTR_PUBLISH_DEVICE } from "../../../../../router/routes"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import AppButton from "../../../../buttons/appButton"
import AppAlert from "../../../../dataDisplay/alerts/appAlert"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import ValidatedRadioGroup from "../../../../inputs/validated/validatedRadioGroup"
import ValidatedAutocomplete from "../../../../inputs/validated/validatedSelect"
import ValidatedTextField from "../../../../inputs/validated/validatedTextField"
import CoreDeviceDetailsSkeleton from "../../../../loading/skeletons/coreDeviceDetailsSkeleton"
import { useFormGenerator } from "../../../../submitApplication/single/formGenerator/useFormGenerator"
import { AppCard } from "../../../../surfaces/appCard"
import DisplayIf from "../../../../wrappers/displayIf"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import { LCT_PHASES_OPTIONS } from "../../formSpecs/evdcSpec"
import { EVACDeviceFormData } from "../../types/providerTypes"

interface ICoreDeviceDetailsProps {
    context: Context<IDeviceFormContextValue<EVACDeviceFormData>>
}

const mapRegisterNameToValues = (
    formData: EVACDeviceFormData
): { [name: string]: NonNullable<unknown> | undefined | null } => ({
    [EVAC_MANUFACTURER_REG]: formData.manufacturerId,
    [EVAC_MODEL_REG]: formData.deviceModel,
    [EVAC_MODEL_REF_REG]: formData.deviceModelRef,
    [EVAC_BARCODE_REG]: formData.barcodeNumber,
    [EVAC_PHASES_REG]: formData.phases,
    [EVAC_VOLTAGE_REG]: formData.voltage,
    [EVAC_MAX_CURRENT_REG]: formData.maxCurrentAmpPerPhase,
    [EVAC_AC_ONLY_REG]: formData.acOnlyOutput,
    [EVAC_V2G_CAPABILITY_REG]: formData.v2gCapability,
    [EVAC_V2G_EXPORT_CAPACITY_REG]: formData.v2gExportCapacity,
    [EVAC_V2G_COMPLETION_DATE_REG]: formData.v2gCompletionDate,
    [EVAC_V2G_G99_REG]: formData.v2gG99gbG999niBoth,
    [EVAC_V2G_TTR_STATUS_REG]: formData.v2gTtrStatus,
    [EVAC_ADDITIONAL_COMMENTS_REG]: formData.comments,
})

/** Displays a form page that allows the user to enter the core
 * information about a device the want to publish
 */
const CoreEVACDeviceDetailsForm = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const {
        currentIndex,
        setCurrentIndex,
        formData,
        setFormData,
        formIsLoading,
        saveDraft,
        saveDraftLoading,
        errors: contextErrors,
    } = useContext(context)

    const isFirstPage = currentIndex === 0

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useFormGenerator(mapRegisterNameToValues(formData))

    const getUpdatedFormData = (): EVACDeviceFormData => ({
        ...formData,
        deviceModel: watch(EVAC_MODEL_REG) as string,
        deviceModelRef: watch(EVAC_MODEL_REF_REG) as string,
        barcodeNumber: watch(EVAC_BARCODE_REG) as string,
        phases: watch(EVAC_PHASES_REG) as number,
        voltage: watch(EVAC_VOLTAGE_REG) as number,
        maxCurrentAmpPerPhase: watch(EVAC_MAX_CURRENT_REG) as number,
        acOnlyOutput: watch(EVAC_AC_ONLY_REG) as boolean,
        v2gCapability: watch(EVAC_V2G_CAPABILITY_REG) as boolean,
        v2gExportCapacity: watch(EVAC_V2G_EXPORT_CAPACITY_REG) as number,
        v2gCompletionDate: watch(EVAC_V2G_COMPLETION_DATE_REG) as string, // Date
        v2gG99gbG999niBoth: watch(EVAC_V2G_G99_REG) as string,
        v2gTtrStatus: watch(EVAC_V2G_TTR_STATUS_REG) as string,
        comments: watch(EVAC_ADDITIONAL_COMMENTS_REG) as string,
    })

    const updateCoreEVACDeviceDetails = (): void => {
        const updatedFormData = getUpdatedFormData()
        setFormData(updatedFormData)
    }

    const MAX_COMMENT_CHARACTERS = 500

    const hasV2gCapability = watch(EVAC_V2G_CAPABILITY_REG) as boolean

    const onSaveDraft = (): void => {
        const updatedFormData = getUpdatedFormData()
        setFormData(updatedFormData)
        saveDraft(updatedFormData)
    }

    const onClickBack = isFirstPage
        ? undefined
        : (): void => {
              updateCoreEVACDeviceDetails()
              setCurrentIndex(currentIndex - 1)
          }

    const onClickNext = (): void => {
        updateCoreEVACDeviceDetails()
        setCurrentIndex(currentIndex + 1)
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <>
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVAC_MODEL_REG}
                        label={evacDeviceFormLabelMapping[EVAC_MODEL_REG]}
                        placeholder="Enter device model"
                        tooltip={TT_MODEL}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVAC_MODEL_REF_REG}
                        label={evacDeviceFormLabelMapping[EVAC_MODEL_REF_REG]}
                        placeholder="Enter device model reference"
                        tooltip={TT_EVAC_MODEL_REF}
                    />
                    <ValidatedTextField
                        control={control}
                        required={false}
                        registerName={EVAC_BARCODE_REG}
                        label={evacDeviceFormLabelMapping[EVAC_BARCODE_REG]}
                        placeholder="Enter device barcode number"
                        tooltip={TT_EVAC_BARCODE}
                    />
                    <ValidatedRadioGroup
                        control={control}
                        required
                        registerName={EVAC_PHASES_REG}
                        label={evacDeviceFormLabelMapping[EVAC_PHASES_REG]}
                        radioOptions={LCT_PHASES_OPTIONS}
                        tooltip={TT_DEVICE_PHASES}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVAC_VOLTAGE_REG}
                        label={evacDeviceFormLabelMapping[EVAC_VOLTAGE_REG]}
                        placeholder="Enter device voltage (V)"
                        tooltip={TT_VOLTAGE}
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Voltage must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVAC_MAX_CURRENT_REG}
                        label={evacDeviceFormLabelMapping[EVAC_MAX_CURRENT_REG]}
                        tooltip={TT_EVAC_MAX_CURRENT}
                        placeholder="Enter device Maximum current in Amps per phase"
                        customRules={{
                            pattern: {
                                value: /^\d+([.]\d+)?$/,
                                message:
                                    "Max Current Amps per Phase must be a positive number",
                            },
                        }}
                    />
                    <ValidatedRadioGroup
                        registerName={EVAC_AC_ONLY_REG}
                        label={evacDeviceFormLabelMapping[EVAC_AC_ONLY_REG]}
                        control={control}
                        required
                        radioOptions={{
                            Yes: true,
                            No: false,
                        }}
                    />
                    <ValidatedRadioGroup
                        registerName={EVAC_V2G_CAPABILITY_REG}
                        required
                        label={
                            evacDeviceFormLabelMapping[EVAC_V2G_CAPABILITY_REG]
                        }
                        control={control}
                        radioOptions={{
                            Yes: true,
                            No: false,
                        }}
                    />
                    <DisplayIf condition={hasV2gCapability}>
                        <>
                            <ValidatedTextField
                                control={control}
                                required
                                registerName={EVAC_V2G_EXPORT_CAPACITY_REG}
                                label={
                                    evacDeviceFormLabelMapping[
                                        EVAC_V2G_EXPORT_CAPACITY_REG
                                    ]
                                }
                                placeholder="Enter Device export capacity (kW)"
                                customRules={{
                                    pattern: {
                                        value: /^\d+([.]\d+)?$/,
                                        message:
                                            "Capacity must be a positive number",
                                    },
                                }}
                            />
                            <AppAlert severity="info">
                                <Stack>
                                    Chargers with V2G capability must be
                                    registered twice. Firstly, as a generation
                                    asset and then secondly using this form. If
                                    you have already completed the first step,
                                    please enter the device&rsquo;s generation
                                    system reference below (this is the
                                    reference you received when registering the
                                    device as a generation asset). You
                                    don&rsquo;t need to wait for the generation
                                    device to be assessed before completing this
                                    form
                                    <Box
                                        display="flex"
                                        justifyContent="flex-start"
                                        flexDirection="row"
                                        marginTop={2}
                                    >
                                        <AppButton
                                            variant="outlined"
                                            onClick={() =>
                                                window.open(
                                                    TTR_PUBLISH_DEVICE,
                                                    "_blank"
                                                )
                                            }
                                        >
                                            I need to register the charger as a
                                            generation asset
                                        </AppButton>
                                    </Box>
                                </Stack>
                            </AppAlert>
                            <ValidatedTextField
                                control={control}
                                required
                                registerName={EVAC_V2G_TTR_STATUS_REG}
                                label={
                                    evacDeviceFormLabelMapping[
                                        EVAC_V2G_TTR_STATUS_REG
                                    ]
                                }
                                placeholder="Enter system reference of V2G inverter from the ENA generation asset register"
                            />
                            <ValidatedAutocomplete
                                control={control}
                                required
                                registerName={EVAC_V2G_G99_REG}
                                label={
                                    evacDeviceFormLabelMapping[EVAC_V2G_G99_REG]
                                }
                                options={Object.keys(v2gG99Options)}
                                getOptionLabel={(option) =>
                                    v2gG99Options[option]
                                }
                            />
                        </>
                    </DisplayIf>
                    <ValidatedTextField
                        control={control}
                        required={false}
                        registerName={EVAC_ADDITIONAL_COMMENTS_REG}
                        label={
                            evacDeviceFormLabelMapping[
                                EVAC_ADDITIONAL_COMMENTS_REG
                            ]
                        }
                        placeholder={
                            "Enter additional comments - " +
                            `Max ${MAX_COMMENT_CHARACTERS} characters`
                        }
                        customRules={{
                            maxLength: {
                                value: MAX_COMMENT_CHARACTERS,
                                message: `Max permitted characters: ${MAX_COMMENT_CHARACTERS}`,
                            },
                        }}
                    />
                    <FormErrors
                        errors={errors}
                        getFriendlyName={(key: string) =>
                            evacDeviceFormLabelMapping[key]
                        }
                    />
                    <FormNavigation
                        onClickBack={onClickBack}
                        onClickNext={() => handleSubmit(onClickNext)()}
                        nextText="Next"
                        allDisabled={formIsLoading}
                        onClickMiddle={onSaveDraft}
                        middleDisabled={saveDraftLoading}
                    />
                </>
            </Stack>
        </AppCard>
    )
}

const CoreEVACDeviceDetails = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const { formIsLoading, currentIndex } = useContext(context)

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Core EV Charge Point (AC) Device Details"
                    subHeader="To start, please enter the following core details about the device"
                    index={currentIndex + 1}
                />
                {formIsLoading ? (
                    <CoreDeviceDetailsSkeleton />
                ) : (
                    <CoreEVACDeviceDetailsForm context={context} />
                )}
            </Stack>
        </AppCard>
    )
}

export default CoreEVACDeviceDetails
